import { Edit } from "@mui/icons-material";
import { Button, IconButton, Switch, Typography } from "@mui/material";
import { format } from "date-fns";
import { useCallback } from "react";
import { Link } from "react-router-dom";
import { useUpdatePriceMutation } from "src/api/services/pricesApi";
import { Column, ProductPrice } from "src/api/types";
import { theme } from "src/assets/theme";
import { usePricesDrawer } from "src/context/drawers/PricesDrawerProvider/usePricesDrawer";
import PriceStatusBadge from "./components/PriceStatusBadge";

export const useColumns = (): Column<ProductPrice>[] => {
  const [updatePrice] = useUpdatePriceMutation();
  const { handleOpenUpdatePrice } = usePricesDrawer();

  const handleToggle = useCallback(
    async (row: ProductPrice) => {
      await updatePrice({
        id: row.id,
        active: !row.active,
      }).unwrap();
    },
    [updatePrice],
  );

  const stripeUrl = useCallback(
    (row: ProductPrice) =>
      import.meta.env.PROD
        ? `https://dashboard.stripe.com/prices/${row.stripe_id}`
        : `https://dashboard.stripe.com/test/prices/${row.stripe_id}`,
    [],
  );

  return [
    {
      id: "price_status",
      label: "",
      minWidth: 80,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => <PriceStatusBadge price={row} />,
    },
    {
      id: "edit",
      label: "Edit",
      minWidth: 80,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => (
        <IconButton onClick={() => handleOpenUpdatePrice(row)}>
          <Edit sx={{ color: theme.palette.secondary.main }} />
        </IconButton>
      ),
    },
    {
      id: "active",
      label: "Active",
      minWidth: 50,
      align: "center",
      orderable: false,
      format: (row) => row.active.toString(),
      render: (row) => (
        <Switch
          color="secondary"
          checked={row.active}
          onChange={() => handleToggle(row)}
        />
      ),
    },
    {
      id: "id",
      label: "ID",
      minWidth: 50,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => <Link to={`${row.id}`}>{row.id}</Link>,
    },
    {
      id: "name",
      label: "Name",
      minWidth: 200,
      align: "center",
      orderable: false,
      format: (row) => row.name,
      render: (row) => <Typography>{row.name}</Typography>,
    },
    {
      id: "display_name",
      label: "Display name (Patient)",
      minWidth: 200,
      align: "center",
      orderable: false,
      format: (row) => row.display_name ?? "N/A",
      render: (row) => <Typography>{row.display_name ?? "N/A"}</Typography>,
    },
    {
      id: "amount",
      label: "Amount",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => `$${row.amount.toString()}`,
      render: (row) => <Typography>{row.amount}</Typography>,
    },
    {
      id: "amount_limit",
      label: "Amount Limit",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => {
        if (row.amount_limit == null) {
          return "N/A";
        }

        return `$${row.amount_limit.toString()}`;
      },
      render: (row) => <Typography>{row.amount_limit ?? "N/A"}</Typography>,
    },
    {
      id: "competitor_price",
      label: "Competitor Price",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => {
        if (row.competitor_price == null) {
          return "N/A";
        }

        return `$${row.competitor_price.toString()}`;
      },
      render: (row) => <Typography>{row.competitor_price ?? "N/A"}</Typography>,
    },
    {
      id: "monthly_price",
      label: "Monthly Price",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => {
        if (row.monthly_price == null) {
          return "N/A";
        }

        return `$${row.monthly_price.toString()}`;
      },
      render: (row) => <Typography>{row.monthly_price ?? "N/A"}</Typography>,
    },
    {
      id: "interval_count",
      label: "Interval Count",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => row.interval_count.toString(),
      render: (row) => <Typography>{row.interval_count}</Typography>,
    },
    {
      id: "interval",
      label: "Interval",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => row.interval,
      render: (row) => <Typography>{row.interval}</Typography>,
    },
    {
      id: "number_of_months",
      label: "Number of Months",
      minWidth: 100,
      align: "center",
      orderable: false,
      format: (row) => row.number_of_months.toString(),
      render: (row) => <Typography>{row.number_of_months}</Typography>,
    },
    {
      id: "default_coupon_id",
      label: "Coupon",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => row.coupon?.name ?? "N/A",
      render: (row) => <Typography>{row.coupon?.name ?? "N/A"}</Typography>,
    },
    {
      id: "default_coupon_amount",
      label: "Coupon Amount",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => {
        if (row.coupon?.amount == null) {
          return "N/A";
        }

        return `$${row.coupon?.amount}`;
      },
      render: (row) => {
        if (row.coupon?.amount == null) {
          return "N/A";
        }

        return <Typography>{row.coupon?.amount}</Typography>;
      },
    },
    {
      id: "created_at",
      label: "Created",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => {
        if (row.created_at == null) {
          return "N/A";
        }

        return format(new Date(row.created_at), "MM/dd/yyyy");
      },
      render: (row) => {
        const createdAt =
          row.created_at == null
            ? "N/A"
            : format(new Date(row.created_at), "MM/dd/yyyy");
        return <Typography>{createdAt}</Typography>;
      },
    },
    {
      id: "default_price",
      label: "Default price",
      minWidth: 100,
      align: "center",
      orderable: false,
      format: (row) => row.default_price.toString(),
      render: (row) => <Typography>{row.default_price ? "✔️" : ""}</Typography>,
    },
    {
      id: "default_selection",
      label: "Default selection",
      minWidth: 100,
      align: "center",
      orderable: false,
      format: (row) => row.default_selection.toString(),
      render: (row) => (
        <Typography>{row.default_selection ? "✔️" : ""}</Typography>
      ),
    },
    {
      id: "stripe_id",
      label: "",
      minWidth: 120,
      align: "center",
      orderable: false,
      format: (row) => row.stripe_id,
      render: (row) => (
        <Link to={stripeUrl(row)} target="_blank">
          <Button
            variant="text"
            sx={{
              backgroundColor: "#9966ff",
              color: "white",
            }}
          >
            <Typography fontWeight={700} variant="body1">
              {"Stripe"}
            </Typography>
          </Button>
        </Link>
      ),
    },
  ];
};
